import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Email as EmailType } from '../services/emailService';
import './GmailEmailRenderer.css';

interface GmailEmailRendererProps {
    email: EmailType | null;
    onBack: () => void;
    onDelete?: (id: string) => void;
    isLightMode?: boolean;
    onArchive?: (id: string) => void;
    onToggleImportant?: (id: string) => void;
    isArchived?: boolean;
}

/**
 * Simple Email Renderer for Temp Mail
 * Renders emails safely without reply/forward (temp mail only receives)
 */
const GmailEmailRenderer: React.FC<GmailEmailRendererProps> = ({
    email,
    onBack,
    onDelete,
    isLightMode = true,
    onArchive,
    onToggleImportant,
    isArchived = false,
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeHeight, setIframeHeight] = useState(400);

    // Extract sender initial for avatar
    const senderInitial = useMemo(() => {
        if (!email?.from) return '?';
        const name = email.from.split('<')[0].trim();
        return name.charAt(0).toUpperCase() || '?';
    }, [email?.from]);

    // Generate avatar color based on sender
    const avatarColor = useMemo(() => {
        const colors = [
            '#1a73e8', '#ea4335', '#34a853', '#fbbc04', '#fa7b17',
            '#f538a0', '#a142f4', '#24c1e0', '#5f6368', '#3c4043'
        ];
        if (!email?.from) return colors[0];
        const hash = email.from.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        return colors[Math.abs(hash) % colors.length];
    }, [email?.from]);

    // Format date
    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        if (isToday) {
            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    // Write content to iframe directly
    useEffect(() => {
        if (!iframeRef.current || !email) return;

        const iframe = iframeRef.current;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        // Get content - prefer HTML, fallback to plain text
        let emailBody = '';
        if (email.html && email.html.trim()) {
            emailBody = email.html;
        } else if (email.content) {
            // Convert plain text to HTML
            emailBody = `<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; color: ${isLightMode ? '#202124' : '#e8eaed'}; padding: 16px; white-space: pre-wrap;">${email.content
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\n/g, '<br>')
                }</div>`;
        } else {
            emailBody = '<div style="padding: 20px; color: #666;">No content available</div>';
        }

        const bgColor = isLightMode ? '#ffffff' : '#1f1f1f';
        const textColor = isLightMode ? '#202124' : '#e8eaed';
        const linkColor = isLightMode ? '#1a73e8' : '#8ab4f8';

        // Dark mode overrides - force dark theme on email content
        const darkModeOverrides = !isLightMode ? `
          /* Force dark backgrounds */
          body, html, div, td, th, table, p, span, section, article, header, footer, main, aside {
            background-color: #1f1f1f !important;
            color: #e8eaed !important;
          }
          
          /* Override any white/light backgrounds */
          [style*="background-color: #fff"],
          [style*="background-color: white"],
          [style*="background-color:#fff"],
          [style*="background-color: #ffffff"],
          [style*="background-color:#ffffff"],
          [style*="background: #fff"],
          [style*="background: white"],
          [style*="background:#fff"],
          [style*="background: #ffffff"],
          [style*="background:#ffffff"],
          [bgcolor="#ffffff"],
          [bgcolor="#fff"],
          [bgcolor="white"],
          [style*="background-color: rgb(255"],
          [style*="background-color:rgb(255"] {
            background-color: #1f1f1f !important;
          }
          
          /* Force text colors in dark mode */
          [style*="color: #000"],
          [style*="color:#000"],
          [style*="color: black"],
          [style*="color:black"],
          [style*="color: #333"],
          [style*="color:#333"],
          [style*="color: rgb(0"],
          [style*="color:rgb(0"] {
            color: #e8eaed !important;
          }
          
          /* Keep colorful elements visible */
          a { color: ${linkColor} !important; }
          
          /* Preserve button and accent colors but ensure readability */
          [style*="background-color: #"] {
            color: inherit;
          }
        ` : '';

        // Light mode overrides - force dark text on light backgrounds
        const lightModeOverrides = isLightMode ? `
        /* Force dark text for commonly misused white text styles */
        [style *= "color: #fff"],
            [style*="color:#fff"],
        [style *= "color: white"],
            [style *= "color:white"],
            [style *= "color: #ffffff"],
            [style *= "color:#ffffff"],
            [style *= "color: rgb(255"],
            [style *= "color:rgb(255"] {
            color: #202124!important;
        }
        ` : '';

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" rel="stylesheet">
<style>
    body {
        margin: 0;
        padding: 16px;
        background-color: ${bgColor};
        color: ${textColor};
        font-family: "Google Sans", Roboto, Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        -webkit-font-smoothing: antialiased;
    }
    a { color: ${linkColor}; text-decoration: none; }
    a:hover { text-decoration: underline; }
    
    img { max-width: 100%; height: auto; vertical-align: middle; }
    
    table { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    
    h1, h2, h3, h4, h5, h6 { margin-top: 0; margin-bottom: 10px; font-weight: 500; }
    p { margin-top: 0; margin-bottom: 10px; }
    
    ::-webkit-scrollbar { width: 10px; height: 10px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background-color: rgba(155, 155, 155, 0.3); border-radius: 5px; border: 2px solid transparent; background-clip: content-box; }
    ::-webkit-scrollbar-thumb:hover { background-color: rgba(155, 155, 155, 0.5); }

    ${darkModeOverrides}
    ${lightModeOverrides}
</style>
</head>
<body>${emailBody}</body>
</html>`;

        doc.open();
        doc.write(htmlContent);
        doc.close();

        // Resize iframe to content
        const resize = () => {
            try {
                if (!doc || !doc.body) return;
                const height = doc.body.scrollHeight || doc.documentElement?.scrollHeight || 400;
                setIframeHeight(Math.max(height + 32, 200));
            } catch (e) {
                // Ignore resize errors
            }
        };

        // Resize after content loads
        setTimeout(resize, 100);
        setTimeout(resize, 500);
        setTimeout(resize, 1000);

        // Use ResizeObserver to detect dynamic content changes (images loading, expanding details)
        let observer: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && doc.body) {
            observer = new ResizeObserver(() => resize());
            observer.observe(doc.body);
            if (doc.documentElement) observer.observe(doc.documentElement);
        }

        // Handle link clicks - open in new tab
        const handleLinkClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            if (link) {
                e.preventDefault();
                const href = link.getAttribute('href');
                if (href && !href.startsWith('javascript:')) {
                    window.open(href, '_blank', 'noopener,noreferrer');
                }
            }
        };

        doc.addEventListener('click', handleLinkClick);

        return () => {
            if (observer) observer.disconnect();
            doc.removeEventListener('click', handleLinkClick);
        };

    }, [email, isLightMode]);

    if (!email) {
        return (
            <div className={`email-renderer-empty ${!isLightMode ? 'dark' : ''}`}>
                <div className="empty-icon">📧</div>
                <h3>Select an email to read</h3>
                <p>Choose from your inbox on the left</p>
            </div>
        );
    }

    // Parse sender info
    const fromMatch = email.from.match(/<(.+)>/);
    const fromEmail = fromMatch ? fromMatch[1] : email.from;
    const fromName = email.from.split('<')[0].trim() || email.from;

    const rootClass = isLightMode ? 'email-renderer' : 'email-renderer dark';

    return (
        <div className={rootClass}>
            {/* Simple Toolbar */}
            <div className="email-toolbar">
                <button className="toolbar-btn" onClick={onBack} title="Back to inbox">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                    </svg>
                </button>

                <button className="toolbar-btn" onClick={() => onArchive?.(email.id)} title={isArchived ? "Move to Inbox" : "Archive"}>
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        {isArchived ? (
                            <path fill="currentColor" d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                        ) : (
                            <path fill="currentColor" d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z" />
                        )}
                    </svg>
                </button>

                <button className="toolbar-btn" onClick={() => onDelete?.(email.id)} title="Delete">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        <path fill="currentColor" d="M15 4V3H9v1H4v2h1v13c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V6h1V4h-5zm2 15H7V6h10v13z" />
                    </svg>
                </button>

                <div className="toolbar-spacer"></div>

                <button className="toolbar-btn" onClick={() => window.print()} title="Print">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        <path fill="currentColor" d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" />
                    </svg>
                </button>
            </div>

            {/* Email Content */}
            <div className="email-content">
                {/* Subject */}
                <div className="email-subject-container">
                    <h1 className="email-subject">{email.subject || '(no subject)'}</h1>
                    <div className="email-labels">
                        <span className="email-label">{isArchived ? 'Archived' : 'Inbox'}</span>
                    </div>
                </div>

                {/* Sender Info */}
                <div className="sender-row">
                    <div className="sender-avatar" style={{ backgroundColor: avatarColor }}>
                        {senderInitial}
                    </div>

                    <div className="sender-details">
                        <div className="sender-name-row">
                            <span className="sender-name">{fromName}</span>
                            <span className="sender-email">&lt;{fromEmail}&gt;</span>
                        </div>
                        <div className="sender-to">to me</div>
                    </div>

                    <div className="email-date-actions">
                        <span className="email-date">{formatDate(email.timestamp)}</span>
                        <div className="email-actions">
                            <button
                                className="action-btn"
                                title={email.important ? "Starred" : "Star"}
                                onClick={() => onToggleImportant?.(email.id)}
                            >
                                <svg viewBox="0 0 24 24" width="20" height="20">
                                    {email.important ? (
                                        <path fill="#fbbc04" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                    ) : (
                                        <path fill="currentColor" d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Email Body - Simple iframe */}
                <div className="email-body-wrapper">
                    <iframe
                        ref={iframeRef}
                        className="email-body-iframe"
                        title="Email content"
                        scrolling="no"
                        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        style={{ height: `${iframeHeight}px`, overflow: 'hidden' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default GmailEmailRenderer;
